import React, { useState } from 'react';
import {
    Settings,
    Calculator,
    Building2,
    Ruler,
    DollarSign,
    MapPin,
    ChevronDown,
    ChevronUp,
    PlayCircle,
    RotateCcw,
    Loader2,
    CheckCircle,
    Layers
} from 'lucide-react';
import { fsiV2Service, FSIV2Module } from '../../services/fsiV2';

interface CustomAnalysisParams {
    building_height_m?: number;
    tp_deduction_pct?: number;
    jantri_rate_per_sqm?: number;
    land_use?: string;
    authority?: string;
    unit_size?: number;
    unit_measurement?: 'sqft' | 'sqm' | 'sqyd';
    num_towers?: number;
}

interface CustomAnalysisPanelProps {
    gid: number;
    onAnalysisComplete: (data: any, params: CustomAnalysisParams) => void;
    isLoading?: boolean;
}

const DEFAULT_PARAMS: CustomAnalysisParams = {
    building_height_m: undefined,
    tp_deduction_pct: undefined,
    jantri_rate_per_sqm: undefined,
    land_use: 'Residential',
    authority: undefined,
    unit_size: 1000,
    unit_measurement: 'sqft',
    num_towers: undefined
};

const AUTHORITY_OPTIONS = [
    { value: 'D1_AUDA', label: 'AUDA (Ahmedabad Urban Development Authority)' },
    { value: 'AMC', label: 'AMC (Ahmedabad Municipal Corporation)' },
    { value: 'GUDA', label: 'GUDA (Gandhinagar Urban Development Authority)' },
];

const LAND_USE_OPTIONS = [
    { value: 'Residential', label: 'Residential' },
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Mixed', label: 'Mixed Use' },
    { value: 'Industrial', label: 'Industrial' }
];

const CustomAnalysisPanel: React.FC<CustomAnalysisPanelProps> = ({
    gid,
    onAnalysisComplete,
    isLoading: externalLoading
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [params, setParams] = useState<CustomAnalysisParams>(DEFAULT_PARAMS);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedModules, setSelectedModules] = useState<FSIV2Module[]>(['basic', 'financial', 'sba', 'development_scenarios']);

    const updateParam = (key: keyof CustomAnalysisParams, value: any) => {
        setParams(prev => ({ ...prev, [key]: value }));
    };

    const resetParams = () => {
        setParams(DEFAULT_PARAMS);
    };

    const toggleModule = (module: FSIV2Module) => {
        setSelectedModules(prev =>
            prev.includes(module)
                ? prev.filter(m => m !== module)
                : [...prev, module]
        );
    };

    const runAnalysis = async () => {
        setIsLoading(true);
        try {
            // Build params object with only defined values
            const apiParams: Record<string, any> = {};
            if (params.building_height_m) apiParams.building_height_m = params.building_height_m;
            if (params.tp_deduction_pct) apiParams.tp_deduction_pct = params.tp_deduction_pct;
            if (params.jantri_rate_per_sqm) apiParams.jantri_rate_per_sqm = params.jantri_rate_per_sqm;
            if (params.land_use) apiParams.land_use = params.land_use;
            if (params.authority) apiParams.authority = params.authority;
            if (params.unit_size) apiParams.unit_size = params.unit_size;
            if (params.unit_measurement) apiParams.unit_measurement = params.unit_measurement;
            if (params.num_towers) apiParams.num_towers = params.num_towers;

            const response = await fsiV2Service.getAnalysis(gid, selectedModules, apiParams);
            onAnalysisComplete(response, params);
        } catch (error) {
            console.error('Custom analysis failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const MODULE_OPTIONS: { id: FSIV2Module; label: string; icon: React.ReactNode }[] = [
        { id: 'basic', label: 'Basic FSI', icon: <Building2 className="w-3 h-3" /> },
        { id: 'financial', label: 'Financial', icon: <DollarSign className="w-3 h-3" /> },
        { id: 'compliance', label: 'Compliance', icon: <CheckCircle className="w-3 h-3" /> },
        { id: 'height', label: 'Height', icon: <Ruler className="w-3 h-3" /> },
        { id: 'sba', label: 'SBA', icon: <Layers className="w-3 h-3" /> },
        { id: 'development_scenarios', label: 'Scenarios', icon: <Calculator className="w-3 h-3" /> },
    ];

    const loading = isLoading || externalLoading;

    return (
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            {/* Toggle Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600">
                        <Settings className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                        <div className="font-medium text-gray-800 text-sm">Custom Analysis</div>
                        <div className="text-xs text-gray-500">Set custom parameters for detailed analysis</div>
                    </div>
                </div>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
            </button>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="p-4 border-t border-gray-200 space-y-4">
                    {/* Modules Selection */}
                    <div>
                        <label className="text-xs font-medium text-gray-600 mb-2 block">Modules to Include</label>
                        <div className="flex flex-wrap gap-1.5">
                            {MODULE_OPTIONS.map(mod => (
                                <button
                                    key={mod.id}
                                    onClick={() => toggleModule(mod.id)}
                                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${selectedModules.includes(mod.id)
                                            ? 'bg-indigo-500 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {mod.icon}
                                    {mod.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Parameters Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Building Height */}
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                                <Ruler className="w-3 h-3" />
                                Building Height (m)
                            </label>
                            <input
                                type="number"
                                value={params.building_height_m || ''}
                                onChange={(e) => updateParam('building_height_m', e.target.value ? parseFloat(e.target.value) : undefined)}
                                placeholder="e.g., 35"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        {/* TP Deduction */}
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">
                                TP Deduction (%)
                            </label>
                            <input
                                type="number"
                                value={params.tp_deduction_pct || ''}
                                onChange={(e) => updateParam('tp_deduction_pct', e.target.value ? parseFloat(e.target.value) : undefined)}
                                placeholder="e.g., 10"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        {/* Jantri Rate */}
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                Jantri Rate (₹/m²)
                            </label>
                            <input
                                type="number"
                                value={params.jantri_rate_per_sqm || ''}
                                onChange={(e) => updateParam('jantri_rate_per_sqm', e.target.value ? parseFloat(e.target.value) : undefined)}
                                placeholder="e.g., 25000"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        {/* Land Use */}
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Land Use
                            </label>
                            <select
                                value={params.land_use || 'Residential'}
                                onChange={(e) => updateParam('land_use', e.target.value)}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                {LAND_USE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Authority */}
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                Planning Authority
                            </label>
                            <select
                                value={params.authority || ''}
                                onChange={(e) => updateParam('authority', e.target.value || undefined)}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">Auto detect</option>
                                {AUTHORITY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Number of Towers */}
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                                <Layers className="w-3 h-3" />
                                Number of Towers
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={params.num_towers || ''}
                                onChange={(e) => updateParam('num_towers', e.target.value ? parseInt(e.target.value) : undefined)}
                                placeholder="Auto (1-4)"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                    </div>

                    {/* Unit Size Config */}
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <label className="text-xs font-medium text-gray-600 mb-2 block">Unit Size Configuration</label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Size</label>
                                <input
                                    type="number"
                                    value={params.unit_size || ''}
                                    onChange={(e) => updateParam('unit_size', e.target.value ? parseInt(e.target.value) : undefined)}
                                    placeholder="1000"
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Unit</label>
                                <select
                                    value={params.unit_measurement || 'sqft'}
                                    onChange={(e) => updateParam('unit_measurement', e.target.value)}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="sqft">Square Feet</option>
                                    <option value="sqm">Square Meters</option>
                                    <option value="sqyd">Square Yards</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <button
                                onClick={() => { updateParam('unit_size', 650); updateParam('unit_measurement', 'sqft'); }}
                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded transition-colors"
                            >
                                1BHK (650)
                            </button>
                            <button
                                onClick={() => { updateParam('unit_size', 1000); updateParam('unit_measurement', 'sqft'); }}
                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded transition-colors"
                            >
                                2BHK (1000)
                            </button>
                            <button
                                onClick={() => { updateParam('unit_size', 1400); updateParam('unit_measurement', 'sqft'); }}
                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded transition-colors"
                            >
                                3BHK (1400)
                            </button>
                            <button
                                onClick={() => { updateParam('unit_size', 1800); updateParam('unit_measurement', 'sqft'); }}
                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded transition-colors"
                            >
                                4BHK (1800)
                            </button>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={resetParams}
                            disabled={loading}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Reset
                        </button>
                        <button
                            onClick={runAnalysis}
                            disabled={loading || selectedModules.length === 0}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-colors disabled:opacity-50 font-medium"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Analyzing...
                                </>
                            ) : (
                                <>
                                    <PlayCircle className="w-4 h-4" />
                                    Run Custom Analysis
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomAnalysisPanel;
